import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCurrentPerson } from "@/hooks/use-current-person";
import { invalidateWeeks } from "@/hooks/use-time-entries";
import { timeKeys } from "@/lib/query";
import { getJson, sendJson } from "@/lib/time/api-client";
import type {
  AssignableProjectsResult,
  AssignableSearchResult,
  FavoriteResult,
  ProjectFavoriteResult,
  SetFavoriteInput,
  SetProjectFavoriteInput,
} from "@/schemas/time";

/** GET /api/time/assignable?q=&limit= ; empty parts are left out. */
export function assignableUrl(q: string, limit?: number): string {
  const params = new URLSearchParams();
  const text = q.trim();
  if (text) params.set("q", text);
  if (limit !== undefined) params.set("limit", String(limit));
  const query = params.toString();
  return query ? `/api/time/assignable?${query}` : "/api/time/assignable";
}

export function assignableQueryFn(q: string, limit?: number) {
  return getJson<AssignableSearchResult>(assignableUrl(q, limit));
}

/** The Favorites picker's page size; the layout prefetches this exact query. */
export const FAVORITES_LIMIT = 200;

function assignableQueryOptions(q: string, limit?: number) {
  return queryOptions({
    queryKey: timeKeys.assignable(q, limit),
    queryFn: () => assignableQueryFn(q, limit),
    staleTime: 60_000,
  });
}

/**
 * Server-side search for the pickers (Log time, Favorites). The week grid does not use this: it
 * reads `WeekView.assignable`. The previous page stays on screen while the next search loads.
 */
export function useAssignable(q = "", limit?: number) {
  return useQuery({ ...assignableQueryOptions(q, limit), placeholderData: keepPreviousData });
}

/** Every project the person can log to, for the Favorites window (no search typed). */
export function useAssignableProjects(enabled: boolean) {
  return useQuery({
    queryKey: timeKeys.assignableProjects(),
    queryFn: () => getJson<AssignableProjectsResult>("/api/time/assignable/projects"),
    staleTime: 60_000,
    enabled,
  });
}

/** The most tasks one project can list in the Favorites window (the server's cap). */
const PROJECT_TASKS_LIMIT = 500;

/** One project's tasks, loaded when the Favorites window opens that project. */
export function useProjectTasks(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: timeKeys.assignableProject(projectId),
    queryFn: () =>
      getJson<AssignableSearchResult>(
        `/api/time/assignable?projectId=${encodeURIComponent(projectId)}&limit=${PROJECT_TASKS_LIMIT}`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

/** A task's assignments run to a handful at most; the cap only bounds the read. */
const TASK_ASSIGNMENTS_LIMIT = 50;

/**
 * The person's own assignments to one task, for a shared task link (`?log=open&task=`). The
 * server reads the person from the session; the link only names the task.
 */
export function useTaskAssignments(projectTaskId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: timeKeys.assignableTask(projectTaskId ?? ""),
    queryFn: () =>
      getJson<AssignableSearchResult>(
        `/api/time/assignable?projectTaskId=${encodeURIComponent(projectTaskId ?? "")}&limit=${TASK_ASSIGNMENTS_LIMIT}`,
      ),
    staleTime: 60_000,
    enabled: enabled && projectTaskId !== null,
  });
}

/** True while any read other than an assignable search is loading (the page's own data). */
function isPageRead(query: { queryKey: readonly unknown[] }): boolean {
  const [scope, kind] = query.queryKey;
  return !(scope === timeKeys.assignableAll()[0] && kind === timeKeys.assignableAll()[1]);
}

export function pageReadsInFlight(queryClient: QueryClient): boolean {
  return queryClient.isFetching({ predicate: isPageRead }) > 0;
}

/**
 * Warms the person's assignable list (called from the dashboard layout): the server loads and
 * caches the full list for five minutes, so the first Time page and the Favorites picker open
 * without waiting on the warehouse. It starts only once the person is known and the page's own
 * reads have settled, so it never competes with them at first paint; once started it stays on.
 * The result also fills the Favorites picker's query. A failure is silent; the pages that need
 * the list load it themselves.
 */
export function usePrefetchAssignable() {
  const person = useCurrentPerson();
  const queryClient = useQueryClient();
  // Re-checked whenever the count changes; read live in the effect because on the first
  // render the page's queries have not started yet (the count is still 0).
  const pageFetching = useIsFetching({ predicate: isPageRead });
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (started || !person.data) return;
    if (pageFetching === 0 && !pageReadsInFlight(queryClient)) setStarted(true);
  }, [started, person.data, pageFetching, queryClient]);
  useQuery(prefetchAssignableOptions(started));
}

/** The prefetch's query: the Favorites picker's own, switched on only for a known person. */
export function prefetchAssignableOptions(hasPerson: boolean) {
  return { ...assignableQueryOptions("", FAVORITES_LIMIT), enabled: hasPerson, retry: false };
}

/** Flips `isFavorite` on every loaded search result for the task (ids compare case-insensitively). */
export function patchFavorite(queryClient: QueryClient, result: FavoriteResult): void {
  queryClient.setQueriesData<AssignableSearchResult>(
    { queryKey: timeKeys.assignableAll() },
    // The project list shares the key prefix and has no items: it is left as it is.
    (data) =>
      data && "items" in data
        ? {
            ...data,
            items: data.items.map((item) =>
              item.projectTaskId.toUpperCase() === result.projectTaskId.toUpperCase()
                ? { ...item, isFavorite: result.isFavorite }
                : item,
            ),
          }
        : data,
  );
}

export function useSetFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetFavoriteInput) =>
      sendJson<FavoriteResult>("PUT", "/api/time/favorites", input),
    onSuccess: async (result) => {
      toast.success(result.isFavorite ? "Added to favorites" : "Removed from favorites", {
        id: "time-favorite",
      });
      // The loaded searches change by one flag, so they are patched, not reloaded; they are
      // only marked stale (no refetch) for the next time a picker opens. The week reloads: its
      // grid list is a filter of favorites, which this write can add to or remove from.
      patchFavorite(queryClient, result);
      await Promise.all([
        invalidateWeeks(queryClient),
        // The project list's counts change too; it is re-read now when it is on screen.
        queryClient.invalidateQueries({ queryKey: timeKeys.assignableProjects() }),
        queryClient.invalidateQueries({
          queryKey: timeKeys.assignableAll(),
          refetchType: "none",
        }),
      ]);
    },
    onError: (err) => toast.error(err.message),
  });
}

/**
 * Stars or unstars a whole project. Every task on it changes, so the loaded searches and the
 * week are re-read rather than patched.
 */
export function useSetProjectFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetProjectFavoriteInput) =>
      sendJson<ProjectFavoriteResult>("PUT", "/api/time/favorites/project", input),
    onSuccess: async (result) => {
      toast.success(
        result.isFavorite ? "Project added to favorites" : "Project removed from favorites",
        {
          id: "time-favorite",
        },
      );
      await Promise.all([
        invalidateWeeks(queryClient),
        queryClient.invalidateQueries({ queryKey: timeKeys.assignableAll() }),
      ]);
    },
    onError: (err) => toast.error(err.message),
  });
}
